import * as React from "react";
import { FormattedMessage, defineMessages, injectIntl, WrappedComponentProps } from "react-intl";
import { Route, Routes } from "react-router-dom";
import { connect } from "react-redux";
import { ApplicationState } from "stores";
import Typography from "components/Typography";
import Input from "components/Input";
import Loading from "components/Loading";
import Button from "components/Button";
import { loginRequest, reset } from "stores/loginRequest";
import * as styles from "./styles";

interface IProps {
  loginRequest: (email: string) => void;
  reset: () => void;
  success: boolean;
  loading: boolean;
  error: boolean;
  errorMessage: string;
}

interface IState {
  email: string;
}

const messages = defineMessages({
  emailPlaceholder: {
    id: "containers.Login.emailPlaceholder",
  },
});

const getTranslatedErrorMessage = (error: string) => {
  switch (error) {
    case "email: cannot be empty":
      return <FormattedMessage id="containers.Login.emailCannotBeEmpty" />;
    case "email: must contain a '@'":
      return <FormattedMessage id="containers.Login.emailMustContainAt" />;
    case "email: must have characters on both sides of '@'":
      return <FormattedMessage id="containers.Login.emailMustCharsOnBothSidesOfAt" />;
    case "email: must only contain a single '@'":
      return <FormattedMessage id="containers.Login.emailMustHaveSingleAt" />;
    default:
      return <FormattedMessage id="containers.Login.genericError" />;
  }
};

class Login extends React.Component<IProps & WrappedComponentProps, IState> {
  state = {
    email: "",
  };

  input: HTMLInputElement = null;

  componentDidMount() {
    if (this.input) {
      this.input.focus();
    }
  }

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    this.props.loginRequest(this.state.email);
  };

  render() {
    const { success, loading, error } = this.props;

    if (loading) {
      return (
        <div className={styles.loadingContainer}>
          <Loading />
        </div>
      );
    }

    if (success) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.container}>
            <div className={styles.successHeader}>
              <Typography>
                <FormattedMessage
                  id="containers.Login.checkYourMail"
                  values={{ email: this.state.email }}
                />
              </Typography>
            </div>

            <Typography>
              <FormattedMessage id="containers.Login.successDescription" />
            </Typography>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.container}>
            <h1 className={styles.headerContainer}>
              {getTranslatedErrorMessage(this.props.errorMessage)}
            </h1>
            <Button onClick={this.props.reset}>
              <FormattedMessage id="containers.Login.tryAgain" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <Routes>
            <Route
              path="/error"
              element={
                <React.Fragment>
                  <h1 className={styles.headerContainer}>
                    <FormattedMessage id="containers.Login.headerInvalidNonce" />
                  </h1>
                  <Typography color="grey">
                    <FormattedMessage id="containers.Login.invalidNonceDescription" />
                  </Typography>
                </React.Fragment>
              }
            />
            <Route
              path="*"
              element={
                <React.Fragment>
                  <h1 className={styles.headerContainer}>
                    <FormattedMessage id="containers.Login.header" />
                  </h1>
                  <div className={styles.marginBottom}>
                    <Typography color="normal">
                      <FormattedMessage id="containers.Login.myPageDescription" />
                    </Typography>
                  </div>
                  <Typography color="normal">
                    <FormattedMessage id="containers.Login.typeYourIdentityDescription" />
                  </Typography>
                </React.Fragment>
              }
            />
          </Routes>

          <form className={styles.inputWrapper} onSubmit={this.handleSubmit}>
            <Input
              style={{
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
                borderRight: 0,
              }}
              inputRef={(c) => {
                this.input = c;
              }}
              placeholder={this.props.intl.formatMessage(messages["emailPlaceholder"])}
              type="email"
              value={this.state.email}
              onChange={(e) => this.setState({ email: e.target.value })}
            />
            <Button onClick={this.handleSubmit} style={{ borderRadius: "0 9px 9px 0" }}>
              <FormattedMessage id="containers.Login.send" />
            </Button>
          </form>
        </div>
      </div>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    success: state.loginRequest.requested && state.loginRequest.success,
    loading: state.loginRequest.requested && state.loginRequest.requesting,
    error: state.loginRequest.requested && !state.loginRequest.success,
    errorMessage: state.loginRequest.error,
  }),
  { loginRequest, reset },
)(injectIntl(Login));
